import React from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import AppleAuthButton from 'components/AppleAuth';
import ErrorBoundary from 'components/ErrorBoundary';
import GoogleAuthButton from 'components/GoogleAuthButton';
import MicrosoftAuthButton from 'components/MicrosoftAuthButton';
import { useLoginSuccessEffect } from 'components/loginForm/useLoginSuccessEffect';
import { useRegistrationSource } from 'components/registration/hooks/useRegistrationSource';
import { useRegistrationSuccessEffect } from 'components/registration/hooks/useRegistrationSuccessEffect';
import { MEDIA_WIDTH } from 'lib/constants';
import { getAuthFeatureFlags } from 'selectors/authFeatureFlags.selectors';

import css from './ThirdPartyAuthButtons.css';

export interface ThirdPartyAuthButtonsProps {
    googleAuthOn?: boolean;
    microsoftAuthOn?: boolean;
    appleAuthOn?: boolean;
    onError?: (error: { code: string; reason: string }) => void;
}

const ThirdPartyAuthButtons: React.FC<ThirdPartyAuthButtonsProps> = ({
    onError = () => undefined,
    googleAuthOn,
    microsoftAuthOn,
    appleAuthOn,
}) => {
    const isMobile = useMediaQuery({ maxWidth: MEDIA_WIDTH.mobile });
    const featureFlags = useSelector(getAuthFeatureFlags);
    const sourceLink = useRegistrationSource();
    const loginSuccessEffect = useLoginSuccessEffect();
    const registrationSuccessEffect = useRegistrationSuccessEffect(sourceLink);
    const buttonType = isMobile ? 'icon' : 'standard';

    if (sourceLink === 'ttn') {
        return null;
    }

    return (
        <div className={css.root}>
            {config.GOOGLE_AUTH_ON && googleAuthOn && (
                <GoogleAuthButton
                    key={buttonType}
                    type={buttonType}
                    theme="outline"
                    size="large"
                    errorHandler={onError}
                    onLogin={loginSuccessEffect}
                    onRegister={registrationSuccessEffect}
                />
            )}
            {microsoftAuthOn && (
                <MicrosoftAuthButton
                    type={buttonType}
                    onLogin={loginSuccessEffect}
                    errorHandler={onError}
                    onRegister={registrationSuccessEffect}
                />
            )}
            {featureFlags.APPLE_AUTH_BUTTON &&
                config.APPLE_AUTH_ON &&
                appleAuthOn && (
                    <ErrorBoundary fallback={null}>
                        <AppleAuthButton
                            type={buttonType}
                            errorHandler={onError}
                            onLogin={loginSuccessEffect}
                            onRegister={registrationSuccessEffect}
                        />
                    </ErrorBoundary>
                )}
        </div>
    );
};

export default ThirdPartyAuthButtons;
